import React from 'react';
import axios from 'axios';
import { useMutation, useQuery } from 'react-query';
import Icon from '@mdi/react';
import { mdiCheckCircle, mdiSend } from '@mdi/js';

import LayoutUsuario from '@/components/navegacao/LayoutUsuario';
import PageHeader from '@/components/layout/PageHeader';
import VBox from '@/components/layout/VBox';
import HBox from '@/components/layout/HBox';
import AlertBox from '@/components/layout/AlertBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import CampoSenha from '@/components/Formulario/CampoSenha';
import useWindowSize from '@/lib/useWindowSize';
import { toastError, toastSuccess } from '@/lib/toast';
import { extractApiError } from '@/lib/apiError';

type ConfigTelegram = { configurado: boolean; bot_username: string | null };
type Configuracoes = { telegram: ConfigTelegram };

const Secao = ({ titulo, descricao, children }: { titulo: string; descricao: React.ReactNode; children: React.ReactNode }) => (
    <VBox
        style={{
            gap: '12px', padding: '16px', borderRadius: '8px',
            border: '1px solid var(--tc-color-gray-200)', maxWidth: '760px',
        }}
    >
        <VBox style={{ gap: '4px' }}>
            <span style={{ fontSize: '16px', fontWeight: 600 }}>{titulo}</span>
            <span style={{ fontSize: '13px', color: 'var(--tc-color-gray-700)', lineHeight: 1.5 }}>{descricao}</span>
        </VBox>
        {children}
    </VBox>
);

const SecaoTelegram = ({ config, onChange }: { config: ConfigTelegram; onChange: () => void }) => {
    const [token, setToken] = React.useState('');
    const [erro, setErro] = React.useState<string | undefined>();
    const [trocando, setTrocando] = React.useState(false);

    const mutSalvar = useMutation({
        mutationFn: async () => axios.put('/api/configuracoes/telegram', { token }, { withCredentials: true }),
        onSuccess: () => {
            setToken(''); setErro(undefined); setTrocando(false);
            onChange();
            toastSuccess('Bot do Telegram configurado.');
        },
        onError: (error: unknown) => {
            const { message, fieldErrors } = extractApiError(error, 'Erro ao salvar o token');
            setErro(fieldErrors?.token);
            toastError(fieldErrors?.token ?? message);
        },
    });

    const mutRemover = useMutation({
        mutationFn: async () => axios.delete('/api/configuracoes/telegram', { withCredentials: true }),
        onSuccess: () => { onChange(); toastSuccess('Telegram desligado dos alertas.'); },
        onError: (error: unknown) => toastError(extractApiError(error, 'Erro ao remover o token').message),
    });

    const editando = !config.configurado || trocando;

    return (
        <Secao
            titulo="Telegram dos alertas"
            descricao={(
                <>
                    Os alertas vão pelo bot do Telegram da sua empresa. Crie um com o <b>@BotFather</b> no
                    Telegram (comando <code>/newbot</code>) e cole aqui o token que ele entregar. Depois, cada
                    contato manda <code>/start</code> ao bot — ou o bot é adicionado ao grupo — e aparece para
                    seleção em Alertas › Contatos.
                </>
            )}
        >
            {config.configurado && (
                <HBox style={{ gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Icon path={mdiCheckCircle} size="20px" color="var(--tc-color-success)" />
                    <span>
                        Configurado: <b>@{config.bot_username}</b>
                        {' · '}
                        <a href={`https://t.me/${config.bot_username}`} target="_blank" rel="noreferrer">
                            t.me/{config.bot_username}
                        </a>
                    </span>
                </HBox>
            )}

            {editando ? (
                <VBox style={{ gap: '8px' }}>
                    <CampoSenha
                        label={config.configurado ? 'Novo token do bot' : 'Token do bot'}
                        placeholder="123456789:AAH..."
                        value={token}
                        onChange={(v: string) => { setToken(v); setErro(undefined); }}
                        error={erro}
                    />
                    <span style={{ fontSize: '12px', color: 'var(--tc-color-gray-600)' }}>
                        O token é conferido com o Telegram antes de ser salvo e não volta a ser exibido.
                    </span>
                    <HBox style={{ gap: '8px' }}>
                        <Botao
                            variant="primary"
                            icon={mdiSend}
                            disabled={!token.trim() || mutSalvar.isLoading}
                            onClick={() => mutSalvar.mutate()}
                        >
                            {mutSalvar.isLoading ? 'Conferindo...' : 'Salvar'}
                        </Botao>
                        {trocando && (
                            <Botao variant="none-secondary" onClick={() => { setTrocando(false); setToken(''); setErro(undefined); }}>
                                Cancelar
                            </Botao>
                        )}
                    </HBox>
                </VBox>
            ) : (
                <HBox style={{ gap: '8px' }}>
                    <Botao variant="outline-primary" onClick={() => setTrocando(true)}>Trocar token</Botao>
                    <BotaoPerigoso
                        variant="outline-danger"
                        confirmation="Remover o bot? Os alertas deixam de ir pelo Telegram até outro ser configurado."
                        onConfirm={() => mutRemover.mutate()}
                    >
                        Remover
                    </BotaoPerigoso>
                </HBox>
            )}
        </Secao>
    );
};

const Page = () => {
    const [, , isMobile] = useWindowSize();

    const query = useQuery({
        queryKey: ['configuracoes'],
        queryFn: async () => {
            const res = await axios.get<{ result: Configuracoes }>('/api/configuracoes', { withCredentials: true });
            return res.data.result;
        },
    });

    return (
        <LayoutUsuario>
            <VBox style={{ padding: isMobile ? '16px' : '24px', gap: '16px', height: '100%', overflow: 'auto' }}>
                <PageHeader title="Configurações gerais" description="Configurações que valem para toda a conta." />
                {query.isError ? (
                    <AlertBox variant="danger">Erro ao carregar as configurações.</AlertBox>
                ) : query.data ? (
                    <SecaoTelegram config={query.data.telegram} onChange={() => query.refetch()} />
                ) : null}
            </VBox>
        </LayoutUsuario>
    );
};

export default Page;
